import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Plus,
  Trash2,
  FileText,
  Clock,
  MoreHorizontal,
  Folder,
  ChevronRight,
  ChevronDown,
  Pencil,
} from "lucide-react";
import { ConfirmModal } from "../../../components/ui/confirm-modal";
import { fetchJsonCached, invalidateApiCache } from "../../../app/api/cache";

interface Note {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

interface Notebook {
  id: string;
  name: string;
  expanded: boolean;
}

const NB_KEY = "sandok-notebooks";
const NB_MAP_KEY = "sandok-note-notebooks";

function relTime(iso: string, now = Date.now()): string {
  const diff = now - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

export function NotesPanel() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [selected, setSelected] = useState<Note | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [now, setNow] = useState(Date.now());

  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [noteMap, setNoteMap] = useState<Record<string, string>>({}); // noteId → notebookId
  const [isAddingNotebook, setIsAddingNotebook] = useState(false);
  const [newNotebookName, setNewNotebookName] = useState("");
  const [renamingNotebookId, setRenamingNotebookId] = useState<string | null>(
    null,
  );
  const [renameNotebookValue, setRenameNotebookValue] = useState("");
  const [isAddingNoteTo, setIsAddingNoteTo] = useState<string | null>(null);
  const [isAddingUncategorizedNote, setIsAddingUncategorizedNote] =
    useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState("");

  const [confirmState, setConfirmState] = useState<{
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const confirm = (message: string, onConfirm: () => void) =>
    setConfirmState({ message, onConfirm });

  // Load notebooks from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(NB_KEY);
    if (stored) {
      setNotebooks(JSON.parse(stored));
    } else {
      const defaults: Notebook[] = [
        { id: "general", name: "General", expanded: true },
      ];
      setNotebooks(defaults);
      localStorage.setItem(NB_KEY, JSON.stringify(defaults));
    }
    const storedMap = localStorage.getItem(NB_MAP_KEY);
    if (storedMap) setNoteMap(JSON.parse(storedMap));
  }, []);

  function saveNotebooks(nbs: Notebook[]) {
    setNotebooks(nbs);
    localStorage.setItem(NB_KEY, JSON.stringify(nbs));
  }

  function saveNoteMap(map: Record<string, string>) {
    setNoteMap(map);
    localStorage.setItem(NB_MAP_KEY, JSON.stringify(map));
  }

  const fetchNotes = useCallback(async () => {
    try {
      setNotes(await fetchJsonCached<Note[]>("/api/notes"));
    } catch {
      // offline
    }
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const handleCreateNoteIn = async (notebookId?: string) => {
    const noteTitle = newNoteTitle.trim() || "Untitled";
    setIsAddingNoteTo(null);
    setIsAddingUncategorizedNote(false);
    setNewNoteTitle("");
    const res = await fetch("/api/notes", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: noteTitle, content: "" }),
    });
    if (res.ok) {
      invalidateApiCache("/api/notes");
      const note: Note = await res.json();
      setNotes((prev) => [note, ...prev]);
      setSelected(note);
      setTitle(note.title);
      setContent(note.content);
      setSaveStatus("");
      if (notebookId) {
        const newMap = { ...noteMap, [note.id]: notebookId };
        saveNoteMap(newMap);
      }
    }
  };

  const handleSelect = (note: Note) => {
    setSelected(note);
    setTitle(note.title);
    setContent(note.content);
    setSaveStatus("");
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaveStatus("Saving…");
    const res = await fetch(`/api/notes/${selected.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, content }),
    });
    if (res.ok) {
      invalidateApiCache("/api/notes");
      const updated: Note = await res.json();
      setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      setSelected(updated);
      setSaveStatus("Saved");
    } else {
      setSaveStatus("Save failed");
    }
  };

  const deleteNote = async (id: string) => {
    await fetch(`/api/notes/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    invalidateApiCache("/api/notes");
    setNotes((prev) => prev.filter((n) => n.id !== id));
    const newMap = { ...noteMap };
    delete newMap[id];
    saveNoteMap(newMap);
    if (selected?.id === id) {
      setSelected(null);
      setTitle("");
      setContent("");
      setSaveStatus("");
    }
  };

  const createNotebook = () => {
    const name = newNotebookName.trim();
    setIsAddingNotebook(false);
    setNewNotebookName("");
    if (!name) return;
    const nb: Notebook = { id: crypto.randomUUID(), name, expanded: true };
    saveNotebooks([...notebooks, nb]);
  };

  const renameNotebook = (id: string) => {
    const name = renameNotebookValue.trim();
    setRenamingNotebookId(null);
    if (!name) return;
    saveNotebooks(notebooks.map((nb) => (nb.id === id ? { ...nb, name } : nb)));
  };

  const deleteNotebook = (id: string) => {
    saveNotebooks(notebooks.filter((nb) => nb.id !== id));
    // Remove notebook assignment for notes in this notebook (they become uncategorized)
    const newMap = { ...noteMap };
    Object.keys(newMap).forEach((noteId) => {
      if (newMap[noteId] === id) delete newMap[noteId];
    });
    saveNoteMap(newMap);
  };

  const toggleNotebook = (id: string) => {
    saveNotebooks(
      notebooks.map((nb) =>
        nb.id === id ? { ...nb, expanded: !nb.expanded } : nb,
      ),
    );
  };

  const notesInNotebook = (nbId: string) =>
    notes.filter((n) => noteMap[n.id] === nbId);
  const uncategorizedNotes = notes.filter((n) => !noteMap[n.id]);

  return (
    <div className="flex h-full w-full overflow-x-hidden bg-surface-container-lowest">
      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          onConfirm={() => {
            confirmState.onConfirm();
            setConfirmState(null);
          }}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${selected ? "hidden md:flex" : "flex"} w-full md:w-[260px] border-r border-outline-variant bg-surface flex-col shrink-0`}
      >
        <div className="p-md border-b border-outline-variant flex items-center justify-between">
          <h2 className="font-label-sm text-secondary uppercase tracking-widest">
            Notebooks
          </h2>
          <div className="flex gap-1">
            <button
              onClick={() => {
                setIsAddingNotebook(true);
                setNewNotebookName("");
              }}
              className="text-outline hover:text-primary transition-colors p-1"
              title="New Notebook"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-sm font-body-md">
          {/* Add notebook form */}
          {isAddingNotebook && (
            <div className="mb-2 px-2">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createNotebook();
                }}
                className="flex items-center gap-2 border border-primary rounded px-2 py-1"
              >
                <Folder size={16} className="text-primary shrink-0" />
                <input
                  autoFocus
                  type="text"
                  value={newNotebookName}
                  onChange={(e) => setNewNotebookName(e.target.value)}
                  onBlur={() => createNotebook()}
                  className="bg-transparent border-none outline-none w-full text-primary font-body-md"
                  placeholder="Notebook name..."
                />
              </form>
            </div>
          )}

          {/* Notebooks */}
          {notebooks.map((nb) => (
            <div key={nb.id} className="mb-1">
              <div
                className="flex items-center justify-between p-2 hover:bg-surface-container-low rounded cursor-pointer group"
                onClick={() => toggleNotebook(nb.id)}
              >
                <div className="flex items-center gap-2 flex-1 overflow-hidden text-secondary hover:text-primary">
                  {nb.expanded ? (
                    <ChevronDown size={16} className="shrink-0" />
                  ) : (
                    <ChevronRight size={16} className="shrink-0" />
                  )}
                  <Folder size={16} className="shrink-0" />
                  {renamingNotebookId === nb.id ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        renameNotebook(nb.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1"
                    >
                      <input
                        autoFocus
                        type="text"
                        value={renameNotebookValue}
                        onChange={(e) => setRenameNotebookValue(e.target.value)}
                        onBlur={() => renameNotebook(nb.id)}
                        className="bg-transparent border-b border-primary outline-none w-full text-primary"
                      />
                    </form>
                  ) : (
                    <span className="truncate font-body-md">{nb.name}</span>
                  )}
                </div>
                {renamingNotebookId !== nb.id && (
                  <div
                    className="flex gap-1 md:opacity-0 md:group-hover:opacity-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => {
                        setRenamingNotebookId(nb.id);
                        setRenameNotebookValue(nb.name);
                      }}
                      className="p-1 hover:bg-surface-variant rounded text-secondary hover:text-primary"
                      title="Rename Notebook"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingUncategorizedNote(false);
                        setIsAddingNoteTo(nb.id);
                        setNewNoteTitle("");
                        if (!nb.expanded) toggleNotebook(nb.id);
                      }}
                      className="p-1 hover:bg-surface-variant rounded text-secondary hover:text-primary"
                      title="New Note"
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      onClick={() =>
                        confirm(
                          `Delete notebook "${nb.name}" and move its notes to uncategorized?`,
                          () => deleteNotebook(nb.id),
                        )
                      }
                      className="p-1 hover:bg-surface-variant rounded text-secondary hover:text-error"
                      title="Delete Notebook"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>

              {nb.expanded && (
                <div className="ml-6 mt-1 flex flex-col gap-0.5">
                  {isAddingNoteTo === nb.id && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleCreateNoteIn(nb.id);
                      }}
                      className="p-1 px-2 border border-primary rounded flex items-center gap-2"
                    >
                      <FileText size={16} className="text-primary shrink-0" />
                      <input
                        autoFocus
                        type="text"
                        value={newNoteTitle}
                        onChange={(e) => setNewNoteTitle(e.target.value)}
                        onBlur={() => handleCreateNoteIn(nb.id)}
                        className="bg-transparent border-none outline-none w-full text-primary font-body-md"
                        placeholder="Note title..."
                      />
                    </form>
                  )}
                  {notesInNotebook(nb.id).map((note) => (
                    <NoteItem
                      key={note.id}
                      note={note}
                      isSelected={selected?.id === note.id}
                      onSelect={() => handleSelect(note)}
                      onDelete={() =>
                        confirm("Delete this note?", () => deleteNote(note.id))
                      }
                    />
                  ))}
                  {notesInNotebook(nb.id).length === 0 && !isAddingNoteTo && (
                    <div className="p-2 text-outline font-label-sm italic ml-2">
                      Empty
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Uncategorized */}
          <div className="mt-2">
            <div className="px-2 py-1 font-label-sm text-outline uppercase tracking-wider flex items-center justify-between">
              <span>Uncategorized</span>
              <button
                onClick={() => {
                  setIsAddingNoteTo(null);
                  setIsAddingUncategorizedNote(true);
                  setNewNoteTitle("");
                }}
                className="p-1 hover:bg-surface-variant rounded text-secondary hover:text-primary"
                title="New uncategorized note"
              >
                <Plus size={14} />
              </button>
            </div>
            <div className="ml-6 mt-1 flex flex-col gap-0.5">
              {isAddingUncategorizedNote && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleCreateNoteIn();
                  }}
                  className="p-1 px-2 border border-primary rounded flex items-center gap-2"
                >
                  <FileText size={16} className="text-primary shrink-0" />
                  <input
                    autoFocus
                    type="text"
                    value={newNoteTitle}
                    onChange={(e) => setNewNoteTitle(e.target.value)}
                    onBlur={() => handleCreateNoteIn()}
                    className="bg-transparent border-none outline-none w-full text-primary font-body-md"
                    placeholder="Note title..."
                  />
                </form>
              )}
              {uncategorizedNotes.map((note) => (
                <NoteItem
                  key={note.id}
                  note={note}
                  isSelected={selected?.id === note.id}
                  onSelect={() => handleSelect(note)}
                  onDelete={() =>
                    confirm("Delete this note?", () => deleteNote(note.id))
                  }
                />
              ))}
              {uncategorizedNotes.length === 0 &&
                !isAddingUncategorizedNote && (
                  <div className="p-2 text-outline font-label-sm italic ml-2">
                    Empty
                  </div>
                )}
            </div>
          </div>
        </div>
      </aside>

      {/* Editor */}
      <div
        className={`${selected ? "flex" : "hidden md:flex"} flex-1 flex-col max-h-full overflow-hidden`}
      >
        {selected ? (
          <>
            <header className="min-h-14 border-b border-outline-variant flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-md sm:px-lg py-2 shrink-0">
              <div className="flex min-w-0 items-center gap-3 text-secondary">
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="md:hidden p-1 -ml-1 text-secondary hover:text-primary"
                  title="Back to notes"
                >
                  <ArrowLeft size={20} />
                </button>
                <span className="font-mono-sm flex min-w-0 items-center gap-2 truncate">
                  <Clock size={14} /> Last edited{" "}
                  {relTime(selected.updatedAt, now)}
                </span>
                {saveStatus && (
                  <span className="font-label-sm text-outline">
                    {saveStatus}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button
                  onClick={handleSave}
                  className="bg-primary text-on-primary font-label-sm px-3 py-1.5 rounded hover:opacity-90 transition-opacity shadow-sm"
                >
                  Save Note
                </button>
                <button
                  onClick={() =>
                    confirm("Delete this note?", () => deleteNote(selected.id))
                  }
                  className="text-outline hover:text-error transition-colors p-1.5"
                  title="Delete Note"
                >
                  <Trash2 size={18} />
                </button>
                <button className="text-outline hover:text-primary transition-colors">
                  <MoreHorizontal size={20} />
                </button>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-[1800px] mx-auto p-md sm:p-xl lg:px-24 lg:py-16">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-h2 sm:text-[40px] sm:leading-[48px] tracking-tight font-black text-primary mb-md bg-transparent border-none outline-none w-full placeholder:text-outline"
                  placeholder="Note Title"
                />
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full min-h-[calc(100vh-260px)] sm:h-[500px] resize-none bg-transparent border-none outline-none text-primary font-body-lg leading-relaxed placeholder:text-outline"
                  placeholder="Start writing..."
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-outline font-body-lg">
            Select a note to view or edit
          </div>
        )}
      </div>
    </div>
  );
}

function NoteItem({
  note,
  isSelected,
  onSelect,
  onDelete,
}: {
  note: Note;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      className={`flex items-center justify-between p-2 rounded cursor-pointer border-l-[3px] group ${
        isSelected
          ? "bg-secondary-container text-primary border-primary"
          : "hover:bg-surface-container-low text-secondary hover:text-primary border-transparent"
      }`}
    >
      <div className="flex items-center gap-2 flex-1 overflow-hidden">
        <FileText
          size={16}
          className={isSelected ? "text-primary shrink-0" : "shrink-0"}
        />
        <span className="truncate font-body-md">
          {note.title || "Untitled"}
        </span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-surface-variant rounded text-secondary hover:text-error shrink-0"
        title="Delete Note"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}
